import React, { useState, useMemo } from 'react';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { useCompany } from '@/context/CompanyContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useSales } from '@/hooks/useSales';
import { useWithdrawals } from '@/hooks/useWithdrawals';
import { useManagementMode } from '@/hooks/useManagementMode';
import { useDashboardLayout } from '@/hooks/useDashboardLayout';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useDashboardAnalysis } from '@/hooks/useDashboardAnalysis';
import { DashboardInsights } from '@/components/dashboard/DashboardInsights';
import { useUserSettings } from '@/hooks/useUserSettings';
import { CompanySetup } from '@/components/company/CompanySetup';
import { MainLayout } from '@/components/layouts/MainLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EditableGrid } from '@/components/dashboard/EditableGrid';
import { DashboardWidget } from '@/components/dashboard/DashboardWidget';
import { WidgetConfigModal } from '@/components/dashboard/WidgetConfigModal';
import { ChartModal } from '@/components/dashboard/ChartModal';
import { EditModeToggle } from '@/components/dashboard/EditModeToggle';
import { SupabaseAddProductModal } from '@/components/inventory/SupabaseAddProductModal';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Plus, ShoppingCart, Package, Users, RotateCcw } from 'lucide-react';
import { AddWidgetButton } from '@/components/dashboard/AddWidgetButton';
import type { Product } from '@/context/SupabaseInventoryContext';
export const Dashboard: React.FC = () => {
  const {
    currentCompany,
    userRole,
    loading: companyLoading
  } = useCompany();
  const {
    products,
    getLowStockProducts
  } = useSupabaseInventory();
  const {
    sales
  } = useSales();
  const {
    withdrawals
  } = useWithdrawals();
  const {
    hasAccess
  } = useSubscription();
  const {
    mode: managementMode
  } = useManagementMode();
  
  // Dashboard layout management
  const {
    widgets,
    isEditMode,
    reorderWidgets,
    updateWidget,
    removeWidget,
    addWidget,
    addWidgetAt,
    toggleEditMode,
    resetToDefault
  } = useDashboardLayout(managementMode || undefined);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [configWidget, setConfigWidget] = useState<any>(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [chartModalContent, setChartModalContent] = useState<React.ReactNode>(null);
  const [chartModalTitle, setChartModalTitle] = useState('');
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [tableModalContent, setTableModalContent] = useState<React.ReactNode>(null);
  const [tableModalTitle, setTableModalTitle] = useState('');
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  // État pour synchroniser les sélections des tableaux entre mode normal et modal
  const [sharedProductSelections, setSharedProductSelections] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const isAdmin = userRole === 'admin';
  const isVendeur = userRole === 'vendeur' || userRole === 'member';

  // Raccourcis clavier
  useKeyboardShortcuts([
    {
      key: 'c',
      callback: () => isVendeur && navigate('/caisse'),
      description: 'Ouvrir la caisse'
    },
    {
      key: 'a',
      callback: () => isAdmin && setIsAddModalOpen(true),
      description: 'Ajouter un produit'
    },
    {
      key: 'e',
      callback: () => toggleEditMode(),
      description: 'Basculer le mode édition'
    }
  ]);

  // Statistiques calculées
  const kpiData = useMemo(() => {
    const lowStockProducts = getLowStockProducts();
    const totalValue = products.reduce((sum, product) => sum + product.price * product.quantity, 0);

    // Statistiques des ventes
    const totalSales = sales.reduce((sum, sale) => sum + sale.total_amount, 0);
    const totalSalesCount = sales.length;

    // Statistiques du mois actuel
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const monthlySales = sales.filter(sale => {
      const saleDate = new Date(sale.sale_date);
      return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
    });
    const monthlySalesRevenue = monthlySales.reduce((sum, sale) => sum + sale.total_amount, 0);

    // Statistiques du jour actuel
    const today = new Date().toDateString();
    const todaySales = sales.filter(sale => new Date(sale.sale_date).toDateString() === today);
    const todayRevenue = todaySales.reduce((sum, sale) => sum + sale.total_amount, 0);

    // Statistiques des retraits
    const totalWithdrawals = withdrawals.reduce((sum, withdrawal) => sum + withdrawal.quantity_withdrawn, 0);
    return {
      totalProducts: products.length,
      totalValue,
      lowStockCount: lowStockProducts.length,
      totalSales: totalSalesCount,
      totalRevenue: totalSales,
      monthlyRevenue: monthlySalesRevenue,
      todayRevenue,
      totalWithdrawals,
      averageOrderValue: totalSalesCount > 0 ? totalSales / totalSalesCount : 0
    };
  }, [products, sales, withdrawals, getLowStockProducts]);

  // Analyse IA du dashboard
  const { settings } = useUserSettings();
  const preferredAnalysisHour = settings.dashboardAnalysisHour || 9;
  const { isAnalyzing, analysis, refreshAnalysis } = useDashboardAnalysis(
    kpiData, 
    preferredAnalysisHour,
    managementMode
  );
  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setIsAddModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingProduct(null);
  };

  const handleConfigureWidget = (widget: any) => {
    setConfigWidget(widget);
    setIsConfigModalOpen(true);
  };

  const handleSaveWidgetConfig = (updatedWidget: any) => {
    updateWidget(updatedWidget);
    setIsConfigModalOpen(false);
    setConfigWidget(null);
  };

  const handleAddWidget = (type: string, config: any, index?: number) => {
    if (typeof index === 'number') {
      addWidgetAt(index, type, config);
    } else {
      addWidget(type, config);
    }
  };

  const handleChartClick = (chartComponent: React.ReactNode, title: string) => {
    setChartModalContent(chartComponent);
    setChartModalTitle(title);
    setIsChartModalOpen(true);
  };

  const handleCloseChartModal = () => {
    setIsChartModalOpen(false);
    setChartModalContent(null);
    setChartModalTitle('');
  };

  const handleTableClick = (tableComponent: React.ReactNode, title: string) => {
    // Si c'est un tableau de produits, on crée une version avec les sélections synchronisées
    if (React.isValidElement(tableComponent)) {
      const element = tableComponent as React.ReactElement;
      // Vérifier si c'est un composant SupabaseProductTable ou s'il contient un id "product-table"
      if (element.props?.id === 'product-table' || 
          (element.type as any)?.name === 'SupabaseProductTable' ||
          title.toLowerCase().includes('produit')) {
        
        // Cloner récursivement pour trouver et modifier le SupabaseProductTable
        const cloneWithProps = (element: React.ReactElement): React.ReactElement => {
          if ((element.type as any)?.name === 'SupabaseProductTable') {
            return React.cloneElement(element, {
              initialSelectedProducts: sharedProductSelections,
              onSelectionChange: setSharedProductSelections
            });
          }
          
          if (element.props?.children) {
            const clonedChildren = React.Children.map(element.props.children, (child) => {
              if (React.isValidElement(child)) {
                return cloneWithProps(child);
              }
              return child;
            });
            return React.cloneElement(element, {}, clonedChildren);
          }
          
          return element;
        };
        
        setTableModalContent(cloneWithProps(element));
      } else {
        setTableModalContent(tableComponent);
      }
    } else {
      setTableModalContent(tableComponent);
    }
    setTableModalTitle(title);
    setIsTableModalOpen(true);
  };

  const handleCloseTableModal = () => {
    setIsTableModalOpen(false);
    setTableModalContent(null);
    setTableModalTitle('');
  };

  // Add "Add Widget" button to widgets when in edit mode
  const displayWidgets = widgets;

  // Show setup if no company
  if (!companyLoading && !currentCompany) {
    return <CompanySetup />;
  }
  if (companyLoading) {
    return <LoadingSpinner message="Chargement du tableau de bord..." />;
  }
  const dashboardActions = <>
      {isVendeur && <Button 
          onClick={() => navigate('/caisse')} 
          className="flex items-center gap-2 bg-gradient-primary hover:shadow-elegant"
          aria-label="Ouvrir la caisse (raccourci: C)"
        >
          <ShoppingCart className="w-4 h-4" />
          Caisse
        </Button>}
      {isAdmin && <Button 
          onClick={() => setIsAddModalOpen(true)} 
          className="flex items-center gap-2 bg-gradient-primary hover:shadow-elegant"
          aria-label="Ajouter un nouveau produit (raccourci: A)"
        >
          <Plus className="w-4 h-4" />
          Ajouter un produit
        </Button>}
      <EditModeToggle 
        isEditMode={isEditMode} 
        onToggle={toggleEditMode}
        onReset={resetToDefault}
        onAddWidget={() => {
          const addButton = document.querySelector('[data-add-widget-trigger]') as HTMLButtonElement;
          if (addButton) {
            addButton.click();
          }
        }}
      />
      {isEditMode && (
        <div className="hidden">
          <AddWidgetButton onAdd={handleAddWidget} data-add-widget-trigger />
        </div>
      )}
    </>;
  
  return <MainLayout title="Tableau de bord" actions={dashboardActions}>
      <div className="space-y-8" role="region" aria-label="Widgets du tableau de bord">
        {/* Analyse IA du dashboard */}
        {analysis && (
          <DashboardInsights
            insights={analysis.insights}
            suggestions={analysis.suggestions}
            analyzedAt={analysis.analyzedAt}
            isAnalyzing={isAnalyzing}
            onRefresh={refreshAnalysis}
          />
        )}
        
        <EditableGrid
          widgets={displayWidgets}
          onReorder={reorderWidgets}
          onConfigure={handleConfigureWidget}
          onRemove={removeWidget}
          onAddWidget={handleAddWidget}
          isEditMode={isEditMode}
        >
          {(widget, index) => (
            <DashboardWidget
              widget={widget}
              kpiData={kpiData}
              managementMode={managementMode}
              onEditProduct={isAdmin ? handleEditProduct : undefined}
              onAddWidget={handleAddWidget}
              isEditMode={isEditMode}
              onChartClick={handleChartClick}
              onTableClick={handleTableClick}
              widgetIndex={index}
              sharedProductSelections={sharedProductSelections}
              onProductSelectionChange={setSharedProductSelections}
            />
          )}
        </EditableGrid>

        {/* Actions rapides pour les vendeurs */}
        {isVendeur && !isEditMode && <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20" role="region" aria-label="Actions rapides">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" aria-hidden="true" />
                Actions rapides
              </CardTitle>
            </CardHeader>
            <CardContent>
              <nav className="flex gap-4" aria-label="Navigation rapide">
                <Button 
                  onClick={() => navigate('/caisse')} 
                  size="lg" 
                  className="bg-gradient-primary hover:shadow-elegant"
                  aria-label="Accéder à la caisse (raccourci: C)"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" aria-hidden="true" />
                  Accéder à la caisse
                </Button>
                {managementMode === 'retrait' && <Button 
                    onClick={() => navigate('/retrait')} 
                    variant="outline" 
                    size="lg"
                    aria-label="Accéder à la gestion des retraits"
                  >
                    <Package className="w-4 h-4 mr-2" aria-hidden="true" />
                    Gestion des retraits
                  </Button>}
              </nav>
            </CardContent>
          </Card>}
      </div>

      {/* Modals */}
      <SupabaseAddProductModal isOpen={isAddModalOpen} onClose={handleCloseModal} editingProduct={editingProduct} />
      <WidgetConfigModal 
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        widget={configWidget}
        onSave={handleSaveWidgetConfig}
      />
      <ChartModal
        isOpen={isChartModalOpen}
        onClose={handleCloseChartModal}
        title={chartModalTitle}
      >
        {chartModalContent}
      </ChartModal>
      <ChartModal
        isOpen={isTableModalOpen}
        onClose={handleCloseTableModal}
        title={tableModalTitle}
      >
        {tableModalContent}
      </ChartModal>
    </MainLayout>
};